import { EventEmitter, inject, Injectable } from '@angular/core';
import { Route } from '@angular/router';
import { DTypeForm, DTypeFormOptions, DTypeFormSelectField, DTypeFormService } from '@grupo-constant/next-dtype/form';
import { DeleteDTypeData, DeleteDTypeDataReply, DType, DTypeCol, DTypeCollectionDN, DTypeFamilies, DTypeRepository, EmbeddedItem, EMBEDDED_COLLECTION_COLUMN_URI, EMBEDDED_ITEM_COLUMN_URI, FlushDTypeDataItem, FlushDTypeDataItemReply, FlushDTypeReply, Partaker, RetrieveDTypeByUri, RetrieveDTypeByUriReply, RetrieveDTypeCollectionDNReply, RetrieveDTypeRepositoryByUri, RetrieveDTypeRepositoryByUriReply, SearchDTypeDataCollection, TABLEWITHFIXEDROWS_URI, TABLEWITHVARIABLEROWS_URI } from '@grupo-constant/next-dtype/types';
import { AvailableLangs, LangDefinition, TranslocoService } from '@ngneat/transloco';
import { BehaviorSubject, catchError, firstValueFrom, map, Observable, of, switchMap, throwError } from 'rxjs';
import { Net2BeepleMethodsEnum } from '../enums/net2beeple-methods.enum';
import { HttpService } from '../http/http.service';
import { JsonReply, RetrieveDTypeDataCollectionReply } from '../http/reply.model';
import { RetrieveDTypeDataCollectionRequest } from '../http/request.model';
import { MasterDataComponent } from '../master/master-data/master-data.component';
import { MasterSummaryComponent } from '../master/master-summary/master-summary.component';
import { DTypeFacadeListComponent } from './dtype-facade-list/dtype-facade-list.component';
import { DTypeToGoLiveScheduleBody, FlushMasterOrderRequest, Net2BeepleLocator, RetrieveDTypeByUriRequest, RetrieveMasterCollectionDN, ToggleDTypeHiddenBody, ToggleDTypeLockBody } from './dtype.types';
import { MasterData } from '../master/master.types';
import { FuseConfirmationService } from '../../../@fuse/services/confirmation/confirmation.service';

export const dtypesRoutes: Route[] = [
    {
        path: 'master',
        component: DTypeFacadeListComponent
    },
    // {
    //     path: 'master/new',
    //     component: MasterDesignComponent,
    //     canDeactivate: [EditObjectGuard],
    // },
    // {
    //     path: 'master/design',
    //     component: MasterDesignComponent,
    //     canDeactivate: [EditObjectGuard],
    // },
    {
        path: 'master/data',
        component: MasterDataComponent,
    },
    {
        path: 'master/summary',
        component: MasterSummaryComponent,
    },
    {
        path: 'masterSystem',
        component: DTypeFacadeListComponent
    },
    {
        path: 'masterSystem/data',
        component: MasterDataComponent,
    },
]
@Injectable({
    providedIn: 'root'
})
export class DTypeService {

    dtypeFamilies: DTypeFamilies;

    columns$ = new BehaviorSubject<DTypeCol[]>([]);
    tableComponentColumns$ = new BehaviorSubject<DTypeCol[]>([]);
    dtype = new BehaviorSubject<DType>(undefined);
    updateDTypeSidebar = new EventEmitter<void>();

    private dTypeFormService = inject(DTypeFormService);
    private fuseConfirmationService = inject(FuseConfirmationService);
    private httpService = inject(HttpService);
    private translocoService = inject(TranslocoService);

    get dtype$(): Observable<DType> {
        return this.dtype.asObservable();
    }

    setDtype(dtype: DType) {
        this.dtype.next(dtype);
    }

    /**
     * Devuelve el modelo del formulario de un datatype
     */
    async getDtypeFormByUri(retrieveDTypeBody: RetrieveDTypeByUriRequest, values?: any, options: { readonly?: boolean; isUpdate?: boolean } = {}): Promise<DTypeForm> {
        let formModel: DTypeForm;
        const formDatatype = await this.getDatatype({ retrieveDTypeBody, includeEmbedded: true });
        let availableLangs: string[] = [];
        for (const lang of (this.translocoService.getAvailableLangs() as AvailableLangs)) {
            availableLangs.push((lang as LangDefinition).id);
        }
        if (formDatatype) {
            const formOptions: DTypeFormOptions = {
                readonly: options?.readonly,
                isUpdate: options?.isUpdate,
                locale: this.translocoService.getActiveLang(),
                availableLangs: availableLangs
            };
            formModel = this.dTypeFormService.buildForm(formDatatype, values, formOptions) as DTypeForm;
        }
        return formModel;
    }

    async getDatatype(args: { retrieveDTypeBody: RetrieveDTypeByUri; includeEmbedded?: boolean }) {
        if (!args?.retrieveDTypeBody) {
            return null;
        }
        const dType = await firstValueFrom(this.retrieveDtypeByUri(args.retrieveDTypeBody));
        if (!dType) {
            return null;
        }

        if (args.includeEmbedded) {
            for (const col of dType.column) {
                if (col.type === EMBEDDED_ITEM_COLUMN_URI || col.type === EMBEDDED_COLLECTION_COLUMN_URI ||
                    col.type === TABLEWITHFIXEDROWS_URI || col.type === TABLEWITHVARIABLEROWS_URI) {
                    (col as any).dType = await this.getDatatype({
                        retrieveDTypeBody: { ...args.retrieveDTypeBody, uri: (col as EmbeddedItem).uri, bindableOptions: true },
                        includeEmbedded: true
                    });
                }
            }
        }

        this.setDtype(dType);
        return dType;
    }

    retrieveDTypeRepositoryByUri(body: RetrieveDTypeRepositoryByUri): Observable<DTypeRepository> {
        return this.httpService.post<RetrieveDTypeRepositoryByUriReply>(Net2BeepleMethodsEnum.retrieveDTypeRepositoryByUri, body)
            .pipe(
                switchMap(async (reply) => {
                    const repository = reply?.collection[body.uri];
                    const dtype = repository?.payload;
                    if (dtype) {
                        for (const col of dtype.column) {
                            if (col.type === EMBEDDED_ITEM_COLUMN_URI || col.type === EMBEDDED_COLLECTION_COLUMN_URI || col.type === TABLEWITHFIXEDROWS_URI ||
                                col.type === TABLEWITHVARIABLEROWS_URI) {
                                (col as any).dType = await this.getDatatype({
                                    retrieveDTypeBody: { ...body, uri: (col as EmbeddedItem).uri },
                                    includeEmbedded: true,
                                });
                            }
                        }
                    }
                    return repository;
                })
            )
    }

    retrieveDTypeDataCollectionByUri(uri: string): Observable<any[]> {
        return this.retrieveDTypeDataCollection({ uri, count: false, columnsForList: false })
            .pipe(map(response => response.data));
    }

    retrieveDTypeDataCollection(request: RetrieveDTypeDataCollectionRequest) {
        return this.httpService.post<RetrieveDTypeDataCollectionReply>(Net2BeepleMethodsEnum.retrieveDTypeDataCollection, request);
    }

    getDtypeFormFromDtype(dtype: DType, values?: any, options: { readonly?: boolean; isUpdate?: boolean } = {}) {
        let availableLangs: string[] = [];
        for (const lang of (this.translocoService.getAvailableLangs() as AvailableLangs)) {
            availableLangs.push((lang as LangDefinition).id);
        }
        const formOptions: DTypeFormOptions = {
            readonly: options?.readonly,
            isUpdate: options?.isUpdate,
            locale: this.translocoService.getActiveLang(),
            availableLangs: availableLangs
        };
        return this.dTypeFormService.buildForm(dtype, values, formOptions) as DTypeForm;
    }

    getExternalFilterDtypeFormFromDtype(masterData: MasterData): DTypeForm {
        if (!masterData?.columnsForExternalFilter || masterData.columnsForExternalFilter.length == 0)
            return null;
        
        const filteredColumns = masterData.column
            .filter(col => masterData.columnsForExternalFilter.includes(col.name))
            .map(col => ({
                ...col,
                mandatory: false,
                readonly: false,
                readonlyOnUpdate: false
            }));
        const externalFilterDType: DType = {
            ...masterData,
            column: filteredColumns
        };

        const form: DTypeForm = this.getDtypeFormFromDtype(externalFilterDType); 
        Object.keys(form.fields)
            .forEach(key => {
            const field = form.fields[key];

            if (field?.browserOpts) {
                field.browserOpts.newLine = false;
                field.browserOpts.alignment = 'LEFT';
                field.browserOpts.inLineWithCaption = false;
                field.browserOpts.forbiddenOptions = [];

                if (field instanceof DTypeFormSelectField) {
                    field.browserOpts.width = 'FOURTH';
                }
            }
        });

        form.control.reset();
        
        return form;
    }


    /**
     * Devuelve el conjunto de maestros para
     * pintar los menús laterales en las pantallas de nodos
     */
    retrieveMasterCollectionDN(body: RetrieveMasterCollectionDN): Observable<[DTypeCollectionDN, Partaker]> {
        return this.httpService.post<RetrieveDTypeCollectionDNReply>(Net2BeepleMethodsEnum.retrieveMasterCollectionDN, body)
            .pipe(map(reply => [(reply?.collection ?? {}) as DTypeCollectionDN, reply?.partaker ?? {}]));
    }

    /**
     * Elimina el elemento con la uri proporcionada
     *
     * @param uri
     */
     deleteNextDType(uri: string) {
        const dialogRef = this.fuseConfirmationService.open({
            title: this.translocoService.translate('warning'),
            message: this.translocoService.translate('deleteDTypeConfirm'),
            actions: {
                confirm: {
                    label: this.translocoService.translate('confirm')
                },
                cancel: {
                    label: this.translocoService.translate('cancel')
                }
            }
        });

        return dialogRef.afterClosed()
            .pipe(
                switchMap((result) => {
                    if (result === 'confirmed') {
                        return this.httpService.post<JsonReply>(Net2BeepleMethodsEnum.deleteDType, { uri })
                        .pipe(
                            catchError((error) => {
                                console.error(error);
                                return throwError(() => error);
                            })
                        );
                    }
                })
            );
    }

    retrieveDTypeBuilderCollection(): Observable<DTypeFamilies> {
        if (!this.dtypeFamilies) {
            return this.httpService.post<any>(Net2BeepleMethodsEnum.retrieveDTypeBuilderCollection, {})
                .pipe(
                    map((response) => {
                        if (!!response) {
                            this.dtypeFamilies = response.collection;
                            return this.dtypeFamilies;
                        }
                        return {};
                    })
                );
        } else {
            return of(this.dtypeFamilies);
        }
    }

    flushDTypeToGoLiveSchedule(body: DTypeToGoLiveScheduleBody) {
        return this.httpService.post<FlushDTypeReply>(Net2BeepleMethodsEnum.flushDTypeRepositoryToGoLiveSchedule, body);
    }

    flushDTypeDataItem(body: FlushDTypeDataItem) {
        return this.httpService.post<FlushDTypeDataItemReply>(Net2BeepleMethodsEnum.flushDTypeDataItem, body)
            .pipe(map(reply => reply?.data));
    }

    flushMasterOrder(request: FlushMasterOrderRequest) {
        return this.httpService.post<JsonReply>(Net2BeepleMethodsEnum.flushMasterOrder, request);
    }

    deleteDTypeData(body: DeleteDTypeData) {
        return this.httpService.post<DeleteDTypeDataReply>(Net2BeepleMethodsEnum.deleteDTypeData, body);
    }

    searchDTypeDataCollection(body: SearchDTypeDataCollection) {
        return this.httpService.post<RetrieveDTypeDataCollectionReply>(Net2BeepleMethodsEnum.searchDTypeDataCollection, body);
    }

    getLocatorFromString(stringLocator: string): Net2BeepleLocator {
        const locator: Net2BeepleLocator = {};
        const splitLocator = stringLocator.split(/[/,]/ig);
        let internalSplit: string[];
        for (const split of splitLocator) {
            internalSplit = split.split('=');
            if (internalSplit.length > 1) {
                locator[internalSplit[0]] = internalSplit[1];
            }
        }
        return locator;
    }

    toggleDTypeLock(body: ToggleDTypeLockBody) {
        return this.httpService.post<FlushDTypeReply>(Net2BeepleMethodsEnum.toggleDTypeRepositoryLock, body);
    }

    toggleDTypeHidden(body: ToggleDTypeHiddenBody) {
        return this.httpService.post<FlushDTypeReply>(Net2BeepleMethodsEnum.toggleDTypeRepositoryHidden, body);
    }

    emitUpdateSidebarEvent() {
        this.updateDTypeSidebar.emit();
    }

    private retrieveDtypeByUri(body: RetrieveDTypeByUri): Observable<DType> {
        return this.httpService.post<RetrieveDTypeByUriReply>(Net2BeepleMethodsEnum.retrieveDTypeByUri, body)
            .pipe(
                map(reply => reply?.collection?.[body.uri])
            );
    }
}
